import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { loginSuccess } from "../store/slices/authSlice";
import { API_BASE_URL } from "../config";
import { TelegramVerifyModal } from "features/two-factor-verify";
import type { AuthResponse } from "entities/user";
import { useUpdateAccessTokenMutation } from "features/auth/api/authApi";
import { useLazyGetUserQuery } from "entities/user/api/userApi";
import {
  getPendingTwoFactorFromUrl,
  type PendingTwoFactorFromUrl,
} from "shared/utils/pending2fa";

const TwitterSuccess: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [updateAccessToken] = useUpdateAccessTokenMutation();
  const [getUser] = useLazyGetUserQuery();
  const [twoFactor, setTwoFactor] = useState<PendingTwoFactorFromUrl | null>(
    () => getPendingTwoFactorFromUrl(),
  );

  useEffect(() => {
    if (twoFactor) return;

    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get("access_token");
    const refreshToken = urlParams.get("refresh_token");
    const userId = urlParams.get("user_id");
    const username = urlParams.get("username");
    const twitterToken = urlParams.get("twitter_token");
    const error = urlParams.get("error") || urlParams.get("auth_error");

    if (error) {
      history.push("/login");
      return;
    }

    if (urlParams.get("twitter_auth") === "success") {
      const finishWithRefreshCookie = async () => {
        try {
          const { access_token } = await updateAccessToken().unwrap();
          const user = await getUser(undefined, true).unwrap();
          dispatch(loginSuccess({ access_token, user }));
          history.replace("/dashboard");
        } catch {
          history.push("/login");
        }
      };

      finishWithRefreshCookie();
      return;
    }

    if (twitterToken && (!accessToken || !refreshToken)) {
      const getTokens = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/oauth/twitter`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              oauth_token: twitterToken,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            dispatch(
              loginSuccess({
                user: data.user,
                access_token: data.access_token,
              }),
            );
            history.replace("/dashboard");
          } else {
            history.push("/login");
          }
        } catch {
          history.push("/login");
        }
      };

      getTokens();
      return;
    }

    if (accessToken && refreshToken && userId && username) {
      const user = {
        id: parseInt(userId, 10),
        username,
        email: `${username}@twitter.user`,
        user_hash: "",
      };

      dispatch(
        loginSuccess({
          user,
          access_token: accessToken,
        }),
      );
      history.replace("/dashboard");
      return;
    }

    const pending = getPendingTwoFactorFromUrl();
    if (pending) {
      setTwoFactor(pending);
      return;
    }

    history.push("/login");
  }, [dispatch, getUser, history, twoFactor, updateAccessToken]);

  const handleTwoFactorVerified = (result: AuthResponse) => {
    dispatch(loginSuccess(result));
    history.replace("/dashboard");
  };

  if (twoFactor) {
    return (
      <>
        <div className="twitter-success">
          <div className="loading-spinner"></div>
          <p>Confirm login with Telegram...</p>
        </div>
        <TelegramVerifyModal
          challengeId={twoFactor.challengeId}
          expiresAt={twoFactor.expiresAt}
          message="Enter the code sent to your Telegram."
          onSuccess={handleTwoFactorVerified}
          onClose={() => history.push("/login")}
        />
      </>
    );
  }

  return (
    <div className="twitter-success">
      <div className="loading-spinner"></div>
      <p>Finalizing Twitter authorization...</p>
    </div>
  );
};

export default TwitterSuccess;
